import { SubscriptionStatus } from "@/clients/models/SubscriptionStatus";
import { SubscriptionPurchaseBackdrop } from "@/components/subscription/subscription-purchase-backdrop";
import { Store } from "@/clients/models/Store";
import { FilledButton } from "@/components/ui/buttons/FilledButton";
import { QueryKeys } from "@/constants/query-keys";
import { useCustomerInfo } from "@/features/settings/hooks/use-customer-info";
import { useSubscriptionOfferings } from "@/features/settings/hooks/use-subscription-products";
import { computeYearlyDiscountPercent } from "@/features/paywall/utils/compute-yearly-discount";
import { usePurchaseAndVerify } from "@/features/paywall/hooks/use-purchase-and-verify";
import { usePaywallViewed } from "@/features/paywall/analytics";
import { useGetSubscriptionStatus } from "@/hooks/requests/user/use-get-subscription-status";
import { Capacitor } from "@capacitor/core";
import { PACKAGE_TYPE, type CustomerInfo } from "@revenuecat/purchases-capacitor";
import { useQueryClient } from "@tanstack/react-query";
import { useCloseOnBackButton } from "@/hooks/ui/use-close-on-back-button";
import { memo, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { PrivacyAgreementContent } from "@/pages/welcome-screen/privacy-content";
import { TermsAndConditionsContent } from "@/pages/welcome-screen/terms-content";
import { SettingModalHeader } from "../../components/setting-modal-header";
import { SubscriptionUpgradeOptionCard } from "./subscription-upgrade-option-card";

interface SubscriptionUpgradePageProps {
  onBack?: () => void;
  /**
   * Whether the page is actually on screen. Settings → Subscription keeps this
   * page mounted off-screen and slides it in, so "Paywall Viewed" must follow
   * this flag rather than mount. Route-mounted callers can leave it out.
   */
  isVisible?: boolean;
}

export default memo(function SubscriptionUpgradePage({
  onBack,
  isVisible = true,
}: SubscriptionUpgradePageProps) {
  usePaywallViewed("upgrade", isVisible);
  const { offerings } = useSubscriptionOfferings();
  const { customerInfo } = useCustomerInfo();
  const { subscriptionStatus } = useGetSubscriptionStatus({ enabled: true });
  const queryClient = useQueryClient();
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const [legalModal, setLegalModal] = useState<"privacy" | "terms" | null>(null);
  useCloseOnBackButton(legalModal !== null, () => setLegalModal(null));

  const {
    isPurchasing,
    isBackdropOpen,
    backdropState,
    startPurchase,
    handleManualRetry,
    handleBackdropCancel,
  } = usePurchaseAndVerify({
    placement: "upgrade",
    logPrefix: "[SubscriptionUpgrade]",
    onUnlocked: async () => {
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.SUBSCRIPTION_CUSTOMER_INFO] });
      onBack?.();
    },
  });

  const isPurchaseFlowBusy = isPurchasing;
  const platform = Capacitor.getPlatform();

  const isSubscribedOnAnotherPlatform = useMemo(() => {
    if (!subscriptionStatus || subscriptionStatus.status !== SubscriptionStatus.ACTIVE) {
      return false;
    }
    const serverStore = subscriptionStatus.store;
    if (!serverStore) return false;
    if (platform === "android" && serverStore === Store.APP_STORE) return true;
    if (platform === "ios" && serverStore === Store.PLAY_STORE) return true;
    return false;
  }, [platform, subscriptionStatus]);

  const availablePackages = useMemo(() => offerings?.current?.availablePackages ?? [], [offerings]);

  const upgradeOptions = useMemo(() => {
    const packageTypeOrder = {
      [PACKAGE_TYPE.ANNUAL]: 0,
      [PACKAGE_TYPE.MONTHLY]: 1,
    } as const;

    const packageCandidates = availablePackages
      .filter(
        (pkg) =>
          pkg.packageType === PACKAGE_TYPE.MONTHLY || pkg.packageType === PACKAGE_TYPE.ANNUAL,
      )
      .sort((a, b) => {
        const orderA =
          packageTypeOrder[a.packageType as PACKAGE_TYPE.MONTHLY | PACKAGE_TYPE.ANNUAL];
        const orderB =
          packageTypeOrder[b.packageType as PACKAGE_TYPE.MONTHLY | PACKAGE_TYPE.ANNUAL];
        return orderA - orderB;
      });

    const monthlyPackage = packageCandidates.find((p) => p.packageType === PACKAGE_TYPE.MONTHLY);
    const yearlyPackage = packageCandidates.find((p) => p.packageType === PACKAGE_TYPE.ANNUAL);
    const yearlyDiscountPercent = computeYearlyDiscountPercent(monthlyPackage, yearlyPackage);

    return packageCandidates.map((pkg) => {
      const isYearly = pkg.packageType === PACKAGE_TYPE.ANNUAL;
      return {
        key: pkg.identifier,
        packageData: pkg,
        title: isYearly ? "Yearly" : "Monthly",
        billingFrequency: isYearly ? "Billed yearly" : "Billed monthly",
        discountText:
          isYearly && yearlyDiscountPercent != null ? `save ${yearlyDiscountPercent}%` : undefined,
      };
    });
  }, [availablePackages]);

  const selectedPackage = useMemo(() => {
    if (!selectedPackageId) return null;
    return availablePackages.find((pkg) => pkg.identifier === selectedPackageId) ?? null;
  }, [availablePackages, selectedPackageId]);

  const normalizedCustomerInfo: CustomerInfo | null = useMemo(() => {
    if (!customerInfo) return null;
    return (
      (customerInfo as { customerInfo?: CustomerInfo }).customerInfo ??
      (customerInfo as CustomerInfo)
    );
  }, [customerInfo]);

  const localActiveProductId = normalizedCustomerInfo?.activeSubscriptions?.[0] ?? null;

  const defaultSelectedPackageId = useMemo(() => {
    if (!localActiveProductId) return null;
    const matchedPackage = availablePackages.find(
      (pkg) => pkg.product.identifier === localActiveProductId,
    );
    return matchedPackage?.identifier ?? null;
  }, [availablePackages, localActiveProductId]);

  useEffect(() => {
    if (selectedPackageId !== null) return;
    if (defaultSelectedPackageId) {
      setSelectedPackageId(defaultSelectedPackageId);
    }
  }, [defaultSelectedPackageId, selectedPackageId]);

  return (
    <div className="flex h-full flex-col bg-[#fcfbfb]">
      <SettingModalHeader title="Upgrade Subscription" onBack={onBack} isModal />

      <p className="px-8 pt-6 text-center text-base font-medium text-black/60">
        Tap a subscription to update to it.
      </p>

      <div className="flex flex-1 flex-col justify-center gap-4 px-[35px]">
        {isSubscribedOnAnotherPlatform ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            You appear to already have an active subscription on another device platform. Please
            manage it from the original store account.
          </div>
        ) : (
          upgradeOptions.map((option) => (
            <SubscriptionUpgradeOptionCard
              key={option.key}
              title={option.title}
              priceString={option.packageData.product.priceString}
              billingFrequency={option.billingFrequency}
              discountText={option.discountText}
              isSelected={selectedPackageId === option.packageData.identifier}
              isDisabled={isPurchaseFlowBusy}
              onClick={() => setSelectedPackageId(option.packageData.identifier)}
            />
          ))
        )}
      </div>

      <div className="flex flex-col items-center gap-3 px-[35px] pb-8">
        {!isSubscribedOnAnotherPlatform && selectedPackage && (
          <FilledButton
            color="primary"
            textCase="normal"
            onClick={() => void startPurchase(selectedPackage)}
            disabled={isPurchaseFlowBusy}
          >
            Upgrade Subscription
          </FilledButton>
        )}
        <div className="flex items-center justify-center gap-4 pt-1">
          <button
            type="button"
            onClick={() => setLegalModal("privacy")}
            className="cursor-pointer text-[13px] leading-normal font-medium text-[#620ad5] underline"
          >
            Privacy Policy
          </button>
          <span aria-hidden="true" className="h-1 w-1 rounded-full bg-black" />
          <button
            type="button"
            onClick={() => setLegalModal("terms")}
            className="cursor-pointer text-[13px] leading-normal font-medium text-[#620ad5] underline"
          >
            Terms of Use
          </button>
        </div>
      </div>

      {legalModal !== null &&
        createPortal(
          <div className="fixed inset-0 z-[1001] flex items-end">
            <div className="fixed inset-0 bg-black/40" onClick={() => setLegalModal(null)} />
            <div className="relative h-[80vh] w-full overflow-y-auto rounded-t-[56px] bg-white p-8">
              {legalModal === "privacy" ? (
                <PrivacyAgreementContent />
              ) : (
                <TermsAndConditionsContent />
              )}
            </div>
          </div>,
          document.body,
        )}

      <SubscriptionPurchaseBackdrop
        isOpen={isBackdropOpen}
        state={backdropState}
        onRetry={handleManualRetry}
        onCancel={handleBackdropCancel}
      />
    </div>
  );
});
